import type { AxiosRequestConfig } from 'axios';
import { tapJournal } from '../journal/journalSync';
import { api } from './http';

export type ExpenseType =
  | 'fuel'
  | 'oil'
  | 'to'
  | 'repair'
  | 'modernization'
  | 'other'
  | 'basing'
  | 'parts'
  | 'airnav';

export type MemberExpenseType = ExpenseType | 'personal';

export type ExpenseCostSplit = 'shares' | 'manual' | 'hours' | 'personal';

export type ExpenseOwnerShare = {
  user_id: number;
  name: string;
  login?: string;
  share: number;
  percent: number;
};

export type Expense = {
  id: string | number;
  expense_date: string;
  expense_type: ExpenseType;
  amount: number;
  cost_split?: ExpenseCostSplit | null;
  payer_ids?: number[];
  payer_shares?: number[];
  flight_ids?: number[];
  title?: string | null;
  comment?: string | null;
};

export type ExpenseTotals = {
  fuel: number;
  oil: number;
  to: number;
  repair: number;
  modernization: number;
  basing: number;
  parts: number;
  airnav: number;
  other: number;
  total: number;
};

export type MemberExpenseTotals = ExpenseTotals & {
  personal: number;
};

export type FinanceYearBucket = {
  year: number;
  hours: number;
  hour_cost: number | null;
  categories: ExpenseTotals;
};

export type FinanceData = {
  year_start: string;
  purchase_date?: string | null;
  hours_all: number;
  hours_year: number;
  hour_cost_all: number | null;
  hour_cost_year: number | null;
  categories: { all: ExpenseTotals; year: ExpenseTotals };
  years?: FinanceYearBucket[];
  items: Expense[];
  items_total?: number;
  items_limit?: number;
  items_offset?: number;
};

export const getFinance = (config?: AxiosRequestConfig) =>
  api.get<FinanceData>('/finance', config);

export type MemberFinanceData = {
  user_id: number;
  aircraft_id: number;
  year_start: string;
  hours_all: number;
  hours_year: number;
  hour_cost_all: number | null;
  hour_cost_year: number | null;
  /** @deprecated use hour_cost_all */
  hours: number;
  /** @deprecated use hour_cost_all */
  hour_cost: number | null;
  total: number;
  by_category: MemberExpenseTotals;
  categories: { all: MemberExpenseTotals; year: MemberExpenseTotals };
  items: {
    id?: string | number;
    expense_date?: string | null;
    entry_date?: string | null;
    expense_type?: MemberExpenseType;
    category: MemberExpenseType;
    amount: number;
    source_kind: string;
    source_id: number;
    note?: string | null;
  }[];
  items_total?: number;
  items_limit?: number;
  items_offset?: number;
};

export const getMemberFinance = (userId?: number) =>
  api.get<MemberFinanceData>('/finance/member', {
    params: userId != null ? { user_id: userId } : undefined,
  });

export const addPersonalExpense = (
  data: {
    expense_date: string;
    amount: number;
    description: string;
  },
  userId?: number
) =>
  tapJournal(
    api.post<Expense & { added: boolean; note?: string }>(
      '/finance/member',
      null,
      {
        params: {
          ...data,
          ...(userId != null ? { user_id: userId } : {}),
        },
      }
    )
  );

export const deletePersonalExpense = (
  expenseId: string | number,
  userId?: number
) =>
  tapJournal(
    api.delete<{ id: string; deleted: boolean }>(
      `/finance/member/${expenseId}`,
      {
        params: userId != null ? { user_id: userId } : undefined,
      }
    )
  );

export const updatePersonalExpense = (
  expenseId: string | number,
  data: {
    expense_date: string;
    amount: number;
    description: string;
  },
  userId?: number
) =>
  tapJournal(
    api.put<Expense & { updated: boolean; note?: string }>(
      `/finance/member/${expenseId}`,
      null,
      {
        params: {
          ...data,
          ...(userId != null ? { user_id: userId } : {}),
        },
      }
    )
  );

export const getFinanceOwners = (asOf?: string) =>
  api.get<{ as_of: string; owners: ExpenseOwnerShare[] }>('/finance/owners', {
    params: asOf ? { as_of: asOf } : undefined,
  });

export const addExpense = (data: {
  expense_date: string;
  expense_type: ExpenseType;
  amount: number;
  cost_split?: ExpenseCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  flight_ids?: number[];
  title?: string;
  comment?: string;
}) =>
  tapJournal(
    api.post<Expense & { added: boolean }>('/finance', null, {
      params: {
        expense_date: data.expense_date,
        expense_type: data.expense_type,
        amount: data.amount,
        cost_split: data.cost_split || 'shares',
        ...(data.payer_ids && data.payer_ids.length
          ? { payer_ids: data.payer_ids.join(',') }
          : {}),
        ...(data.payer_shares && data.payer_shares.length
          ? { payer_shares: data.payer_shares.join(',') }
          : {}),
        ...(data.flight_ids && data.flight_ids.length
          ? { flight_ids: data.flight_ids.join(',') }
          : {}),
        ...(data.title ? { title: data.title } : {}),
        comment: data.comment || '',
      },
    })
  );

export const deleteExpense = (expenseId: string | number) =>
  tapJournal(
    api.delete<{ id: string; deleted: boolean }>(`/finance/${expenseId}`)
  );

export const updateExpense = (
  expenseId: string | number,
  data: {
    expense_date: string;
    expense_type: ExpenseType;
    amount: number;
    cost_split?: ExpenseCostSplit;
    payer_ids?: number[];
    payer_shares?: number[];
    flight_ids?: number[];
    title?: string;
    comment?: string;
  }
) =>
  tapJournal(
    api.put<Expense & { updated: boolean }>(`/finance/${expenseId}`, null, {
      params: {
        expense_date: data.expense_date,
        expense_type: data.expense_type,
        amount: data.amount,
        cost_split: data.cost_split || 'shares',
        ...(data.payer_ids && data.payer_ids.length
          ? { payer_ids: data.payer_ids.join(',') }
          : {}),
        ...(data.payer_shares && data.payer_shares.length
          ? { payer_shares: data.payer_shares.join(',') }
          : {}),
        ...(data.flight_ids && data.flight_ids.length
          ? { flight_ids: data.flight_ids.join(',') }
          : {}),
        ...(data.title ? { title: data.title } : {}),
        comment: data.comment || '',
      },
    })
  );
